import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';

import axios from 'axios';

import { useContainer } from 'unstated-next';
import { ClassInfo, MyDetails, RosterTableData } from '../../components/roster/types';
import { ADMIN_SETTINGS, CLASS_INFO_DATA_PORTAL, CLASS_ME, CLASS_PERMISSIONS, CLASS_STUDENTS_DATA_PORTAL } from '../../config/apiPaths';
import { DEFAULT_SCHOOL_SETTINGS } from '../../config/settingsConfig';
import { GetAdminSettings, SchoolSettings } from '../../types/apiTypes';
import { getUrls } from '../../utils/url';
import { StudentGroupRecord } from '../../../../models/types';
import AdminAuthContainer from './AdminAuthContainer';

export interface RosterProviderProps {
    classUuid: string;
    classId: string | null;
    userUuid: string | null;
    isDemo: string | null;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface RosterContextInterface {
    classUuid: string;
    classId: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    roster: any[];
    selectedStudentId: number | undefined;
    setSelectedStudentId: (selectedUser: number) => void;
    openEditModal: boolean;
    setOpenEditModal: (openEditModdal: boolean) => void;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openDeleteModal: boolean;
    setOpenDeleteModal: (openEditModal: boolean) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    rosterData: { loading: boolean; roster: RosterTableData[] };
    setRosterData: (rosterData: any) => void;
    showAddStudentsButton: boolean;
    userUuid: string | null;
    isDemo: string | null;
    isAuthorized: boolean | null;
    myDetails?: MyDetails;
    adminSettings?: SchoolSettings;
    sendingCalendarInvite: boolean;
    setSendingCalendarInvite: (sendingCalendarInvite: boolean) => void;
    loadRoster: () => void;
    classInfo?: ClassInfo;
    studentGroups?: StudentGroupRecord[];
    onBack?: () => void;
}

const RosterContext = createContext(undefined as unknown as RosterContextInterface);

const { Provider, Consumer } = RosterContext;

const RosterProvider = (props: PropsWithChildren<RosterProviderProps>) => {
    const { classId, classUuid, userUuid, isDemo, narrowStyle, fullScreen, onBack, children } = props;

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);

    const [isLoading, setIsLoading] = useState(true);
    const [rosterData, setRosterData] = useState<any>({ loading: true, roster: [] });
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [selectedStudentId, setSelectedStudentId] = useState<number | undefined>(undefined);
    const [showAddStudentsButton, setShowAddStudentsButton] = useState<boolean>(false);
    const [myDetails, setMyDetails] = useState<MyDetails | undefined>(undefined);
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
    const [sendingCalendarInvite, setSendingCalendarInvite] = useState<boolean>(false);
    const [adminSettings, setAdminSettings] = useState<SchoolSettings>(DEFAULT_SCHOOL_SETTINGS);
    const [classInfo, setClassInfo] = useState<ClassInfo | undefined>(undefined);
    const [studentGroups, setStudentGroups] = useState<StudentGroupRecord[]>([]);
    const { api } = getUrls();

    /**
     * Details are only pulled for members of a class.
     * Admins do not have student details in a class.
     */
    const fetchMyDetails = useCallback(async () => {
        try {
            const { data } = await axios.get(`${api}${CLASS_ME}?class_id=${classId}&user_uuid=${userUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`);
            setMyDetails(data);
        } catch (err) {
            setMyDetails(undefined);
        }
    }, [api, classId, userUuid, authMode, showDemoContent]);

    const fetchAuth = useCallback(async () => {
        if (isAuthorized !== null) return;

        if (!classUuid || !userUuid) {
            setIsAuthorized(false);
            return;
        }

        try {
            const { data: { isPrivileged } } = await axios.get(`${api}${CLASS_PERMISSIONS}?class_id=${classUuid}&user_uuid=${userUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`);

            if (isPrivileged) {
                setIsAuthorized(true);
            } else {
                setIsAuthorized(false);
            }
        } catch (err) {
            console.error(err);
            setIsAuthorized(false);
        }
    }, [api, classUuid, isAuthorized, userUuid, authMode, showDemoContent]);

    const loadRoster = useCallback(async () => {
        const { data }: { data: RosterTableData[] } = await axios.get(`${api}${CLASS_STUDENTS_DATA_PORTAL}?user_uuid=${userUuid}&class_id=${classUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`);
        setRosterData({
            loading: false,
            roster: data
        });
    }, [api, classUuid, authMode, showDemoContent, userUuid]);

    useEffect(() => {
        fetchMyDetails();
    }, [fetchMyDetails]);

    useEffect(() => {
        fetchAuth();
    }, [fetchAuth]);

    useEffect(() => {
        if (isAuthorized === null) return;

        if (isAuthorized === false) {
            setIsLoading(false);
            setRosterData({ roster: [], loading: false });
            return;
        }

        const getAdminSettings = async () => {
            const { data } = await axios.get<GetAdminSettings>(`${api}${ADMIN_SETTINGS}`);
            setAdminSettings(data.data);
        };

        const fetchRoster = async () => {
            await loadRoster();
            setIsLoading(false);
        };

        const fetchClass = async () => {
            const { data } = await axios.get(`${api}${CLASS_INFO_DATA_PORTAL}?user_uuid=${userUuid}&class_id=${classUuid}&with_info=true&auth_mode=${authMode}&demo_content=${showDemoContent}`);
            const { student_groups: studentGroupsArray, class_name: className, sid, meeting_id: meetingId, data: classData } = data;

            // eslint-disable-next-line
            setClassInfo({ class_name: className, sid: sid, meeting_id: meetingId });
            setStudentGroups(studentGroupsArray);
            const isEmpty = Object.keys(classData).length === 0;
            setShowAddStudentsButton(!isEmpty);
        };

        Promise.all([getAdminSettings(), fetchRoster(), fetchClass()]);
    }, [classUuid, api, userUuid, isAuthorized, loadRoster, authMode, showDemoContent]);

    return (
        <Provider
            value={{
                classId,
                classUuid,
                loading: rosterData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                roster: rosterData.roster,
                openEditModal,
                setOpenEditModal,
                openDeleteModal,
                setOpenDeleteModal,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                selectedStudentId,
                setSelectedStudentId,
                rosterData,
                setRosterData,
                showAddStudentsButton,
                userUuid,
                isDemo,
                isAuthorized,
                myDetails,
                adminSettings,
                sendingCalendarInvite,
                setSendingCalendarInvite,
                loadRoster,
                classInfo,
                studentGroups,
                onBack
            }}
        >
            {children}
        </Provider>
    );
};

export { RosterContext, RosterProvider, Consumer as RosterConsumer };
